// The Call flows tab: one segmented control switches between the interactive
// phone-menu guide (default) and the existing diagram / outline viewer, while
// the selected location survives the switch. Admin actions (import, add,
// edit) are forwarded to whichever view is showing.

import { useEffect, useState } from 'react';
import { BookOpen, Network, List, Plus, Upload, RefreshCw } from 'lucide-react';
import type { CallFlow } from '../api/client';
import { CallFlowsViewer, type DialTarget } from './CallFlowsViewer';
import { PhoneMenuGuide } from './phoneMenu/PhoneMenuGuide';
import type { PresenceInfo } from './phoneMenu/lib/callFlowStatus';

type Mode = 'guide' | 'diagram' | 'outline';
const MODE_KEY = 'qd.callflows.mode';

export interface CallFlowsTabProps {
  flows: CallFlow[];
  accentColor: string;
  dirById: Record<string, DialTarget>;
  onDial: (item: DialTarget) => void;
  isAdmin?: boolean;
  onAddLocation?: () => void;
  onEditLocation?: (flow: CallFlow) => void;
  onImport?: () => void;
  /** Opens the nightly auto-sync dialog (admins). */
  onSync?: () => void;
  /** Base of the browser phone-menu URL, e.g. https://host/goodlife/menu. */
  menuBaseUrl?: string;
  initialLocationId?: string;
  presence?: Map<string, PresenceInfo>;
  presenceState?: 'ok' | 'consent' | 'unavailable' | 'loading';
  presenceConsentUrl?: string;
  onVisibleAgents?: (ids: string[]) => void;
}

function readMode(): Mode {
  try {
    const v = window.sessionStorage.getItem(MODE_KEY);
    if (v === 'guide' || v === 'diagram' || v === 'outline') return v;
  } catch { /* storage may be blocked */ }
  return 'guide';
}

export function CallFlowsTab({ flows, accentColor, dirById, onDial, isAdmin, onAddLocation, onEditLocation, onImport, onSync, menuBaseUrl, initialLocationId, presence, presenceState, presenceConsentUrl, onVisibleAgents }: CallFlowsTabProps) {
  const [mode, setMode] = useState<Mode>(() => readMode());
  const [locationId, setLocationId] = useState<string | null>(() => initialLocationId || null);
  useEffect(() => { try { window.sessionStorage.setItem(MODE_KEY, mode); } catch { /* ignore */ } }, [mode]);

  const flow = flows.find(f => f.id === locationId) || flows[0];
  if (!flow) return null;

  const seg = (value: Mode, Icon: typeof BookOpen, label: string) => (
    <button type="button" onClick={() => setMode(value)}
      className={`px-2.5 py-1 text-xs inline-flex items-center gap-1 ${mode === value ? 'bg-white shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'} rounded`}
      style={mode === value ? { color: accentColor } : undefined}>
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap print:hidden">
        <div className="inline-flex bg-gray-100 rounded-md p-1 gap-0.5">
          {seg('guide', BookOpen, 'Guide')}
          {seg('diagram', Network, 'Diagram')}
          {seg('outline', List, 'Outline')}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            {onImport && (
              <button onClick={onImport} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700">
                <Upload className="w-3.5 h-3.5" />Import from Teams
              </button>
            )}
            {onSync && (
              <button onClick={onSync} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700" title="Nightly sync from Teams">
                <RefreshCw className="w-3.5 h-3.5" />Sync
              </button>
            )}
            {onAddLocation && (
              <button onClick={onAddLocation} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700">
                <Plus className="w-3.5 h-3.5" />Add location
              </button>
            )}
          </div>
        )}
      </div>
      {mode === 'guide' ? (
        <PhoneMenuGuide
          flows={flows} flow={flow} onSelectFlow={setLocationId}
          dirById={dirById} onDial={onDial} dialMode="teams" accentColor={accentColor}
          isAdmin={isAdmin} onEdit={onEditLocation}
          presence={presence} presenceState={presenceState} presenceConsentUrl={presenceConsentUrl} onVisibleAgents={onVisibleAgents}
          browserUrl={`${menuBaseUrl || `${window.location.origin}/menu`}/${encodeURIComponent(flow.id)}`}
        />
      ) : (
        <CallFlowsViewer
          flows={flows} accentColor={accentColor} dirById={dirById} onDial={onDial}
          isAdmin={isAdmin} onAddLocation={onAddLocation} onEditLocation={onEditLocation} onImport={onImport}
          mode={mode} onModeChange={m => setMode(m)} locationId={flow.id} onSelectLocation={setLocationId}
        />
      )}
    </div>
  );
}
